/**
 * **An invitation still waiting**: the envelope with the days it has waited, who it went to and
 * when, how long the OFFER still stands (client-onboarding R4's fourteen days, off the roster's
 * `invitationExpiresAt`), a message the trainer can send privately, and „Wyślij ponownie".
 *
 * ── Only the offer's clock is claimed ────────────────────────────────────────────────────────
 * The canon also printed "link w mailu żyje 24 h". That clock is Supabase's setting, not a value
 * this app or the backend reads, so the card does not state it — a number nobody can check is the
 * kind that goes stale silently.
 *
 * ── The resend is gated like the roster's ────────────────────────────────────────────────────
 * `canResendInvitation` (an invited roster ENTRY, never an offer to an existing account) — the
 * server refuses the other kind (client-onboarding R6). A resend is not a close: the invitation
 * still waits, and the card says it was sent rather than striking itself.
 *
 * ── The message is copied, never sent ────────────────────────────────────────────────────────
 * The invitation carries no shareable link, so the text points at the mail rather than pasting one.
 * `navigator.clipboard.writeText` can be refused (no permission, an insecure origin); the card then
 * says so and leaves the text selectable.
 *
 * ── The message needs the address, and a failed roster is said ──────────────────────────────
 * The message names the mailbox the invitation went to, so it is drawn only once the roster has
 * given the address; worded without one it told the client to look in "". A roster that could not
 * be read is said in the card's own words rather than leaving the address, the offer's clock and
 * the resend to vanish without one.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { buildClientRoster, canResendInvitation, invitationClock, rosterQueryOptions } from '~/entities/client';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import ghost from '~/shared/ui/ghostButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { TaskAct } from '../lib/useTaskAct';
import { useHomeFormats } from '../lib/useHomeFormats';
import type { TaskDensity, TaskSlots } from '../lib/taskWrites';
import type { AttentionItem } from '../model/attentionItems';
import { invitationValidity } from '../model/taskContent';
import { taskWriteTargets } from '../model/writeTargets';
import { ActButton, ActGlyphMark, ActRefusal } from './TaskActs';
import styles from './taskBlocks.module.css';

const RESEND = 'resend';

interface InvitationBlockProps {
  readonly item: AttentionItem;
  readonly density: TaskDensity;
  readonly todayIso: string;
  readonly slots: TaskSlots;
  readonly act: TaskAct;
}

export function InvitationBlock({ item, density, todayIso, slots, act }: InvitationBlockProps) {
  const { t } = useTranslation();
  const { shortDate } = useHomeFormats();
  const roster = useQuery(rosterQueryOptions());
  const gate = useSkeletonGate(roster.isPending);
  const invitationId = taskWriteTargets(item).invitationId;
  // The one roster entry this invitation is, found once; its row and its clock are read off it alone.
  const entry = invitationId === null ? undefined : roster.data?.find((dto) => dto.invitationId === invitationId);
  const row = entry === undefined ? null : (buildClientRoster([entry], null).rows[0] ?? null);
  const validity = invitationValidity(
    entry === undefined || invitationId === null ? null : invitationClock([entry], invitationId),
    todayIso,
  );
  const writes = slots.writes;
  const first = item.fullName.split(' ')[0] ?? item.fullName;
  const email = row?.email ?? null;
  // The trainer's own edit, or `null` for the message as worded — which waits for the roster's
  // address rather than freezing an empty one into the field on the first render.
  const [draft, setDraft] = useState<string | null>(null);
  const message =
    draft ?? (email === null ? null : t('web:tasks.blocks.invitation.message', { first, email }));
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
  const days = item.triage.daysPending;

  if (gate.waiting) {
    return (
      <div className={`${String(styles.stack)} ${String(styles[density])}`} aria-busy="true">
        <span className={`${String(styles.reserve)} ${String(styles.reserveInvitation)} ${gate.skeleton ? String(skel.bar) : ''}`} />
        <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
      </div>
    );
  }

  return (
    <div className={`${String(styles.stack)} ${String(styles[density])} ${gate.reveal}`}>
      <div className={styles.invitation}>
        <span className={styles.envelope} aria-hidden="true">
          <svg viewBox="0 0 132 96" width="132" height="96" fill="none" strokeLinejoin="round" strokeLinecap="round">
            <rect x="8" y="18" width="116" height="70" rx="10" stroke="currentColor" strokeWidth="1.6" />
            <path d="M12 24 66 60l54-36" stroke="currentColor" strokeWidth="1.6" />
            <path d="M12 84 50 52M120 84 82 52" stroke="currentColor" strokeWidth="1.4" />
            <circle cx="112" cy="20" r="17" className={styles.envelopeSeal} />
          </svg>
          {days === null ? null : (
            <span className={styles.envelopeFigure}>
              {days}
              <small className={styles.envelopeUnit}>{t('web:tasks.content.days', { count: days })}</small>
            </span>
          )}
        </span>
        <div className={styles.invitationText}>
          <p className={styles.invitationHead}>{t('web:tasks.blocks.invitation.head')}</p>
          <p className={styles.invitationMeta}>
            {validity.sentOn === null
              ? null
              : t('web:tasks.blocks.invitation.sent', { date: shortDate(validity.sentOn) })}
            {email === null ? null : (
              <>
                {' '}
                {t('web:tasks.blocks.invitation.to')} <b>{email}</b>
              </>
            )}
          </p>
          {validity.daysLeft === null || validity.expiresOn === null ? null : (
            <p className={styles.invitationMeta}>
              {validity.daysLeft === 0
                ? t('web:tasks.blocks.invitation.lastDay')
                : t('web:tasks.blocks.invitation.valid', {
                    count: validity.daysLeft,
                    date: shortDate(validity.expiresOn),
                  })}
            </p>
          )}
          <p className={styles.note}>
            {roster.isError ? t('web:tasks.blocks.readFailed') : t('web:tasks.blocks.invitation.tip')}
          </p>
        </div>
      </div>

      {density === 'desk' && message !== null ? (
        <div className={styles.message}>
          <label className={styles.quoteLabel} htmlFor={`invite-message-${item.key}`}>
            {t('web:tasks.blocks.invitation.messageLabel')}
          </label>
          <textarea
            id={`invite-message-${item.key}`}
            className={styles.messageField}
            value={message}
            rows={2}
            onChange={(event) => {
              setDraft(event.target.value);
              setCopy('idle');
            }}
          />
          <div className={styles.messageFoot}>
            {copy === 'failed' ? <span className={styles.refused}>{t('web:tasks.blocks.invitation.copyFailed')}</span> : null}
            <button
              type="button"
              className={`${String(ghost.ghost)} ${String(styles.actGhost)} ${String(styles.act)}`}
              onClick={() => {
                navigator.clipboard.writeText(message).then(
                  () => {
                    setCopy('copied');
                    slots.announce(t('web:tasks.blocks.invitation.copied'));
                  },
                  () => {
                    setCopy('failed');
                    slots.announce(t('web:tasks.blocks.invitation.copyFailed'));
                  },
                );
              }}
            >
              <ActGlyphMark glyph="copy" />
              {copy === 'copied' ? t('web:tasks.blocks.invitation.copied') : t('web:tasks.blocks.invitation.copy')}
            </button>
            {writes !== undefined && row !== null && invitationId !== null && canResendInvitation(row) ? (
              <ActButton
                controller={act}
                tone="ink"
                glyph="resend"
                label={t('web:tasks.blocks.acts.resend')}
                run={{
                  act: RESEND,
                  closes: false,
                  done: t('web:tasks.blocks.invitation.resent'),
                  write: () => writes.resendInvitation(invitationId),
                }}
              />
            ) : null}
          </div>
          <ActRefusal controller={act} acts={[RESEND]} />
        </div>
      ) : null}
    </div>
  );
}
